import React from 'react';
import { STATI, MOTIVI, MOTIVI_CERVED, etichettaMotivo, Q_MINIMO } from '../pages/campagneData';

/* La barra dei filtri della pagina Campagne: una riga sola che va a capo sugli schermi
   stretti. Qui dentro NON si costruisce nessun filtro per il backend — si modifica solo
   il modulo; la traduzione in query e' di `costruisciFiltro` (campagneData.js), unico
   posto da cui partono sia la lista sia le azioni di massa. */

// Il modulo vuoto. `campagna_id` NON sta qui: e' la campagna scelta in alto, e
// "Azzera filtri" non deve farti saltare fuori dalla campagna che stai guardando.
export const FILTRI_VUOTI = {
  stato: '', motivo: '', provincia: '', tipo_impresa: '', tipo_linea: '',
  attiva: null, ha_piva: false, ha_email: false, q: '',
  // liste CERVED: il tipo di lista, e due spunte che hanno senso solo per quelle
  tipo: '', ex_tim: false, ftth_nuova: false,
};

/** Quanti filtri sono attivi (la campagna scelta non conta). */
export function filtriAttivi(filtri) {
  const f = filtri || {};
  return Object.keys(FILTRI_VUOTI).filter((k) => {
    const v = f[k];
    return k === 'attiva' ? typeof v === 'boolean' : !!v && v !== FILTRI_VUOTI[k];
  }).length;
}

/* `mostraTipo`: il selettore del tipo di lista, solo quando c'e' almeno una lista CERVED e
   nessuna campagna scelta (con una campagna scelta il tipo e' gia' deciso).
   `mostraCerved`: le spunte ex clienti TIM e fibra in arrivo, solo quando si guardano liste
   CERVED — sulle TIM quei dati non esistono e filtrerebbero via tutto. */
export default function CampagnaFiltri({
  filtri, onCambia, onAzzera, province, tipiImpresa, mostraTipo, mostraCerved, motiviCerved,
}) {
  const f = filtri || {};
  const set = (patch) => onCambia({ ...f, ...patch });
  const attivi = filtriAttivi(f);

  return (
    <div className="camp-filtri">
      {/* La ricerca parte da Q_MINIMO lettere (campagneData): sotto quella soglia il
          filtro non parte, e va detto — altrimenti si digita una lettera, non cambia
          niente, e la casella sembra rotta. */}
      <input className="camp-cerca" value={f.q || ''}
        placeholder={`Cerca nella ragione sociale (almeno ${Q_MINIMO} lettere)…`}
        title={`La ricerca parte da ${Q_MINIMO} lettere`}
        onChange={(e) => set({ q: e.target.value })} />
      {(f.q || '').trim().length > 0 && (f.q || '').trim().length < Q_MINIMO && (
        <span className="camp-nota-inline">
          ancora {Q_MINIMO - (f.q || '').trim().length}
          {' '}{Q_MINIMO - (f.q || '').trim().length === 1 ? 'lettera' : 'lettere'}
        </span>
      )}

      <select value={f.stato || ''} onChange={(e) => set({ stato: e.target.value })}>
        <option value="">Ogni stato</option>
        {STATI.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
      </select>

      <select className="camp-sel-largo" value={f.motivo || ''} onChange={(e) => set({ motivo: e.target.value })}>
        <option value="">Ogni motivo</option>
        {MOTIVI.filter((m) => motiviCerved || !MOTIVI_CERVED.includes(m))
          .map((m) => <option key={m} value={m}>{etichettaMotivo(m)}</option>)}
      </select>

      {/* Provincia e tipo impresa arrivano dal CSV di TIM (maiuscoli: "TARANTO",
          "PERSONA GIURIDICA") e il backend li confronta esatti: i suggerimenti sono
          ricavati dalle righe gia' caricate, cosi' si sceglie il valore giusto invece
          di indovinarne la forma. Resta possibile scriverne uno non ancora comparso. */}
      <input list="camp-province" value={f.provincia || ''} placeholder="Provincia"
        onChange={(e) => set({ provincia: e.target.value })} />
      <datalist id="camp-province">
        {(province || []).map((p) => <option key={p} value={p} />)}
      </datalist>

      <input list="camp-tipi" value={f.tipo_impresa || ''} placeholder="Tipo impresa"
        onChange={(e) => set({ tipo_impresa: e.target.value })} />
      <datalist id="camp-tipi">
        {(tipiImpresa || []).map((t) => <option key={t} value={t} />)}
      </datalist>

      <select value={f.tipo_linea || ''} onChange={(e) => set({ tipo_linea: e.target.value })}>
        <option value="">Fissi e cellulari</option>
        <option value="fisso">Solo fissi</option>
        <option value="mobile">Solo cellulari</option>
      </select>

      <select value={f.attiva === true ? 'si' : f.attiva === false ? 'no' : ''}
        onChange={(e) => set({ attiva: e.target.value === 'si' ? true : e.target.value === 'no' ? false : null })}>
        <option value="">Accese e spente</option>
        <option value="si">Indagine accesa</option>
        <option value="no">Indagine spenta</option>
      </select>

      <label className="camp-spunta">
        <input type="checkbox" checked={!!f.ha_piva} onChange={(e) => set({ ha_piva: e.target.checked })} />
        Ha partita IVA
      </label>
      <label className="camp-spunta">
        <input type="checkbox" checked={!!f.ha_email} onChange={(e) => set({ ha_email: e.target.checked })} />
        Ha email
      </label>

      {mostraTipo && (
        <select value={f.tipo || ''} onChange={(e) => set({ tipo: e.target.value })}
          title="Le liste TIM hanno il numero troncato; le CERVED non hanno il numero ma la partita IVA certa; quelle da import hanno un nome, un comune e al massimo un cellulare">
          <option value="">Tutte le liste</option>
          <option value="tim">Solo liste TIM</option>
          <option value="cerved">Solo liste CERVED</option>
          <option value="generica">Solo liste da import</option>
        </select>
      )}
      {mostraCerved && (
        <>
          <label className="camp-spunta" title="Aziende che sono state clienti TIM (fisso o mobile)">
            <input type="checkbox" checked={!!f.ex_tim} onChange={(e) => set({ ex_tim: e.target.checked })} />
            Ex clienti TIM
          </label>
          <label className="camp-spunta" title="La fibra FTTH arriva entro 30 o 90 giorni">
            <input type="checkbox" checked={!!f.ftth_nuova}
              onChange={(e) => set({ ftth_nuova: e.target.checked })} />
            Fibra in arrivo
          </label>
        </>
      )}

      {attivi > 0 && (
        <button type="button" className="camp-azzera" onClick={onAzzera}>
          Azzera filtri ({attivi})
        </button>
      )}
    </div>
  );
}
